import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface BlockedStateAction {
  label: string
  to?: string
  onClick?: () => void
}

interface BlockedStateProps {
  icon?: LucideIcon
  title: string
  description: string
  action?: BlockedStateAction
  autoFocus?: boolean
}

export function BlockedState({
  icon: Icon = Building2,
  title,
  description,
  action,
  autoFocus = true,
}: BlockedStateProps) {
  const ctaRef = useRef<HTMLButtonElement & HTMLAnchorElement>(null)

  useEffect(() => {
    if (autoFocus && ctaRef.current) ctaRef.current.focus()
  }, [autoFocus])

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center gap-4 py-24 text-center"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Icon className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="space-y-1">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
      </div>
      {action && (
        action.to ? (
          <Button asChild size="sm">
            <Link
              to={action.to}
              ref={ctaRef as React.Ref<HTMLAnchorElement>}
            >
              {action.label}
            </Link>
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={action.onClick}
            ref={ctaRef as React.Ref<HTMLButtonElement>}
          >
            {action.label}
          </Button>
        )
      )}
    </div>
  )
}
